import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Play, Clock, Calendar, Search, Filter } from 'lucide-react';
import { mockData } from '../mock';
import SEOHead from '../components/SEOHead';
import SocialShare from '../components/SocialShare';

const TalksCategory = ({ category }) => {
  const [searchTerm, setSearchTerm] = useState('');
  
  // Filter talks by category
  const categoryTalks = mockData.latestTalks.filter(talk => 
    talk.category.toLowerCase().includes(category.toLowerCase())
  );

  return (
    <div className="space-y-8">
      {/* Search Bar */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search talks..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>
        <button className="px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center space-x-2">
          <Filter size={20} />
          <span>Filter</span>
        </button>
      </div>

      {/* Talks Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {categoryTalks.map((talk) => (
          <div key={talk.id} className="group bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300">
            <div className="relative h-56 overflow-hidden">
              <img
                src={talk.thumbnail}
                alt={talk.title}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                <div className="w-20 h-20 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Play className="text-emerald-700" size={32} fill="currentColor" />
                </div>
              </div>
              <div className="absolute bottom-3 right-3 bg-black/70 backdrop-blur-sm px-3 py-1 rounded-full text-white text-sm flex items-center space-x-1">
                <Clock size={14} />
                <span>{talk.duration}</span>
              </div>
            </div>
            <div className="p-6">
              <div className="text-xs font-semibold text-emerald-700 mb-2">{talk.category}</div>
              <h3 className="font-bold text-gray-900 mb-3 text-lg group-hover:text-emerald-700 transition-colors">
                {talk.title}
              </h3>
              <p className="text-sm text-gray-600 mb-4 line-clamp-3">{talk.description}</p>
              <div className="flex items-center space-x-2 text-xs text-gray-500">
                <Calendar size={14} />
                <span>{new Date(talk.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* YouTube Embed Section */}
      <div className="bg-gradient-to-br from-gray-50 to-emerald-50 rounded-2xl p-8">
        <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Featured Talk</h3>
        <div className="max-w-4xl mx-auto">
          <div className="relative" style={{ paddingBottom: '56.25%', height: 0 }}>
            <iframe
              src="https://www.youtube.com/embed/dQw4w9WgXcQ"
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute top-0 left-0 w-full h-full rounded-xl shadow-lg"
            ></iframe>
          </div>
        </div>
      </div>
    </div>
  );
};

const Talks = () => {
  const { category } = useParams();

  const categories = [
    { id: 'sunya', name: 'Sunya Talks', description: 'Meditation and spiritual teachings' },
    { id: 'ganotherapy', name: 'Ganotherapy', description: 'Ganoderma research and wellness' },
    { id: 'shang-han-lun', name: 'Shang Han Lun', description: 'Classical Chinese Medicine' },
    { id: 'herbology', name: 'Herbology', description: 'Herbal medicine studies' },
    { id: 'herbal', name: 'Herbal Talk', description: 'Traditional remedies' },
    { id: 'coffee', name: 'Coffee Talk', description: 'Casual wellness discussions' },
    { id: 'reflexology', name: 'Reflexology', description: 'Healing techniques' }
  ];

  const currentCategory = category || 'sunya';
  const currentCategoryData = categories.find(c => c.id === currentCategory) || categories[0];

  return (
    <div className="min-h-screen pt-20">
      <SEOHead 
        title="My Talks - Educational Videos"
        description="Watch Dr. Lim Siow Jin's educational talks on Sunya meditation, Ganotherapy, Shang Han Lun, Traditional Chinese Medicine, herbology, and wellness. Learn from a master teacher."
        keywords="Dr. Lim Siow Jin talks, Sunya meditation videos, Ganotherapy teachings, Shang Han Lun series, herbology talks, TCM education"
        
      />
      
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-700 via-emerald-600 to-teal-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">My Talks</h1>
              <p className="text-xl text-emerald-100">Educational content on meditation, wellness, and traditional medicine</p>
            </div>
            <SocialShare 
              title="My Talks - Dr. Lim Siow Jin"
              description="Educational content on meditation, wellness, and traditional medicine."
            />
          </div>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="bg-white border-b border-gray-200 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-1 overflow-x-auto">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/talks/${cat.id}`}
                className={`px-6 py-4 font-medium text-sm whitespace-nowrap border-b-2 transition-colors ${
                  currentCategory === cat.id
                    ? 'border-emerald-600 text-emerald-700'
                    : 'border-transparent text-gray-600 hover:text-emerald-700 hover:border-gray-300'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">{currentCategoryData.name}</h2>
          <p className="text-gray-600">{currentCategoryData.description}</p>
        </div>
        <TalksCategory category={currentCategoryData.name} />
      </div>
    </div>
  );
};

export default Talks;